import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

import { createCustomError, isErrorCodeExist } from '@/shared/api/errorHandler'
import { useApiError } from '@/shared/composables/useApiError/'
import { autoBindDecorator } from './api.utils'
import { CommonService } from './services/CommonService'
import { PolicyService } from './services/PolicyService'
import { QuoteService } from './services/QuoteService'
import { TravellerService } from './services/TravellerService'
import { OcrService } from './services/OcrService'

class ApiErrors {
  static NO_NAME_ERROR = 'No name error'
  static NETWORK_ERROR = 'Network error'
}

export const apiBuilder = (config: AxiosRequestConfig) => {
  if ('retryDelay' in axios.defaults) {
    axios.defaults.retryDelay = 100
  }

  const http: AxiosInstance = axios.create(config)

  const onFulfilled = function (response: AxiosResponse) {
    if (response.data && response.data.success !== undefined && !response.data.success) {
      useApiError(response)
      if (isErrorCodeExist(response.data.errorMessage)) {
        return Promise.reject(createCustomError(response.data.errorMessage, { ...response.data }))
      } else {
        return Promise.reject(response.data.errorMessage || ApiErrors.NO_NAME_ERROR)
      }
    }
    // @todo раньше было условие и возвращался payload
    return Promise.resolve(response)
  }

  // @todo функция отличается от других проектов, есть ретрай
  const onRejected = function (response: any) {
    const config = response.config

    if (!config || !config.retry) {
      useApiError(response)
      return Promise.reject(response)
    }

    const data = response.data || response.response.data
    const retryMessageIgnoreList = config.retryMessageIgnoreList || []

    if (data.errorMessage && retryMessageIgnoreList.includes(data.errorMessage)) {
      useApiError(response)
      return Promise.reject(response)
    }

    config.__retryCount = config.__retryCount ?? 1

    if (config.__retryCount >= config.retry) {
      useApiError(response)
      return Promise.reject(response)
    }

    config.__retryCount += 1

    const backoff = new Promise<void>(function (resolve) {
      setTimeout(function () {
        resolve()
      }, config.retryDelay || 1)
    })

    return backoff.then(function () {
      return http(config)
    })
  }

  const beforeSend = (config: InternalAxiosRequestConfig) => {
    if (import.meta.env.MODE !== 'development') {
      config.headers['QIC-Product-Code'] = 'visitors'
    }
    return config
  }

  http.interceptors.request.use(beforeSend)
  http.interceptors.response.use(onFulfilled, onRejected)

  // let withMocks: boolean | undefined = false

  // try {
  //   /*
  //     Hack:
  //       import.meta.env is not working in tests
  //       process.env is not working in development

  //       So we need this try/catch to avoid errors
  //    */
  //   withMocks = import.meta.env.VUE_APP_API_WITH_MOCKS === 'true' || process.env.VITEST === 'true'
  // } catch (err) {
  //   // @ts-ignore
  // }

  return {
    quoteService: autoBindDecorator(new QuoteService(http)),
    commonSeervice: autoBindDecorator(new CommonService(http)),
    policyService: autoBindDecorator(new PolicyService(http)),
    travellerService: autoBindDecorator(new TravellerService(http)),
    ocrService: autoBindDecorator(new OcrService(http)),
  }
}
